import http from 'common/http'
import { geoserverFetch, gisUrl, isGisUrl } from 'common/geoserver'
import {
  clearFeatures,
  fieldFetched,
  fieldsFetched,
  harvestedFieldsFetched,
  featuresFetched,
  featuresFetchedA,
  featuresFetchedB,
  editFieldDetails,
  removeField,
  removeFields,
  setDefaultExtent,
  setTenantExtent,
  setFeaturesOverview,
  layerInfoFetched,
  layerInfoAFetched,
  layerInfoBFetched,
  fieldsHarvestedAFetched,
  fieldsHarvestedBFetched,
  fieldsImported,
  downloadingLayer,
  updateFeaturesOverview,
  cancelEditFieldName,
  setYear as setYearAction,
} from 'actions/field'
import { appReady, addEvent, closeModal, showModal } from 'actions/app'
import {
  stopDrawingBoundary,
  pointInfoRes,
  swathInfoRes,
  areaInfoRes,
  elevationProfileRes,
  setExtent,
} from 'actions/map'

import { clearHarvests } from 'actions/harvest'
import { isEmpty, chunk } from 'es-toolkit/compat'
import Alert from '@mui/material/Alert'
import i18next from 'i18next'

export const createField = (data, callback?: () => void) => {
  return (dispatch) => {
    return http.post('/field', data).then((res) => {
      dispatch(fieldFetched(res.data))
      dispatch(appReady())
      dispatch(addEvent('Field created message', 'Field created title'))
      dispatch(closeModal())
      dispatch(fetchFields())
      callback && callback()
    })
  }
}

export const createMultipleFields = (dataArray, callback?: () => void) => {
  return (dispatch) => {
    const chunkSize = 25
    const chunks = chunk(dataArray, chunkSize)
    return Promise.all(
      chunks.map((item) => http.post('/field/field-import/multiple', item)),
    )
      .then((results) => {
        dispatch(fieldsFetched(results[results.length - 1].data))
        dispatch(addEvent('fields_create_success', 'fields_created'))
        dispatch(closeModal())
        return {
          data: results[results.length - 1].data,
          message: 'Fields created',
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 500) {
          alert(i18next.t('failed_to_save_fields'))
        } else {
          alert(i18next.t('invalid_field_data'))
        }
      })
  }
}

export const editField = (idField, data, callback?: () => void) => {
  return (dispatch) => {
    return http.put(`/field/${idField}`, data).then((res) => {
      dispatch(editFieldDetails(res.data))
      dispatch(fieldFetched(res.data))
      dispatch(cancelEditFieldName())
      dispatch(closeModal())
      callback && callback()
    })
  }
}

export const editMultipleField = (data, callback?: () => void) => {
  return (dispatch) => {
    return http.post(`/field/update-fields`, data).then((res) => {
      dispatch(editFieldDetails(res.data))
      dispatch(fieldFetched(res.data))
      dispatch(cancelEditFieldName())
      callback && callback()
    })
  }
}

export const deleteField = (idField, callback?: () => void) => {
  return (dispatch) => {
    return http.delete(`/field/${idField}`).then(() => {
      dispatch(removeField(idField))
      dispatch(closeModal())
      dispatch(fetchBBox())
      callback && callback()
    })
  }
}
export const deleteFields = (idField, callback?: () => void) => {
  return (dispatch) => {
    return http.delete(`/field/delete-fields/multiple/${idField}`).then(() => {
      dispatch(removeFields(idField))
      dispatch(closeModal())
      dispatch(fetchBBox())
      callback && callback()
    })
  }
}

export const fetchFields = (year?, crop?, clientId?, farmIds?) => {
  return (dispatch, getState) => {
    // If year is not explicitly passed, try to get it from state
    const state = getState()
    let effectiveYear = year
    if (!effectiveYear) {
      effectiveYear = state.field?.year
    }

    const params: Record<string, unknown> = {
      minimalist: true,
      year: effectiveYear,
      crop,
      clientId,
    }

    if (farmIds) {
      params.farmIds = Array.isArray(farmIds) ? `[${farmIds}]` : `[${farmIds}]`
    }

    return http
      .get('/field', {
        params,
      })
      .then((res) => {
        const results = res.data.results || res.data
        dispatch(fieldsFetched(results))
        // If we fetched with harvest counts, populate the harvested fields state
        // to avoid a second redundant call
        if (effectiveYear) {
          dispatch(harvestedFieldsFetched(results))
        }
      })
  }
}

export function fetchFieldsPaginated(
  page = 1,
  pageSize = 10,
  includeSvgs = false,
  search = '',
  includeDeleted = '',
) {
  return http.get('/field', {
    params: {
      page,
      pageSize,
      includeSvgs,
      search: search || undefined,
      archived: includeDeleted || undefined,
    },
  })
}

export const fetchFieldsByFarm = (farmId: number) => {
  return http.get('/field', {
    params: {
      farmIds: farmId,
      minimalist: true,
    },
  })
}

export const fetchDeletedFields = () => {
  return http
    .get('/field', {
      params: {
        archived: 'true',
        includeSvgs: false,
      },
    })
    .then((res) => res.data.results)
}

export const saveMultipleFeatures = (payload) => {
  return (dispatch) => {
    return http
      .post('/field/multiple-features', payload)
      .then((res) => {
        dispatch(stopDrawingBoundary())
        dispatch(featuresFetched(res.data))
        dispatch(updateFeaturesOverview(res.data))
        dispatch(fetchTenantExtent())
      })
      .catch(() => {
        // let ids = payload.map(value => value['id'])
        // dispatch(deleteFields(ids))
      })
  }
}

export const saveFeatures = (idField, geojson, disableDraw = true) => {
  return (dispatch) => {
    return http
      .post(`/field/${idField}/features`, { geojson })
      .then((res) => {
        if (disableDraw) dispatch(stopDrawingBoundary())
        dispatch(featuresFetched(res.data))
        dispatch(updateFeaturesOverview(res.data))
        dispatch(addEvent('Boundary saved'))
        dispatch(fetchTenantExtent())
      })
      .catch(() => {
        dispatch(removeField(idField))
      })
  }
}

export const editFeatures = (
  idField,
  geojson,
  harvestId = null,
  harvestYear = null,
) => {
  //Always pass year in put request to not reprocess previous years
  return (dispatch) => {
    return http
      .put(`/field/${idField}/features`, {
        geojson,
        harvestId: harvestId,
        harvestYear: harvestYear,
      })
      .then((res) => {
        dispatch(stopDrawingBoundary())
        dispatch(featuresFetched(res.data))
        dispatch(updateFeaturesOverview(res.data))
        dispatch(addEvent('Boundary saved'))
        dispatch(fetchTenantExtent())
      })
      .catch(() => {
        dispatch(removeField(idField))
      })
  }
}

export const fetchFeatures = (idField, year = undefined) => {
  const params = year !== undefined ? { params: { year: year } } : undefined
  return (dispatch) => {
    return http.get(`/field/${idField}/features`, params).then((res) => {
      dispatch(featuresFetched(res.data))
    })
  }
}

export const fetchFeaturesA = (idField, year = undefined) => {
  const params = year !== undefined ? { params: { year: year } } : undefined
  return (dispatch) => {
    return http.get(`/field/${idField}/features`, params).then((res) => {
      dispatch(featuresFetchedA(res.data))
    })
  }
}

export const fetchFeaturesB = (idField, year = undefined) => {
  const params = year !== undefined ? { params: { year: year } } : undefined
  return (dispatch) => {
    return http.get(`/field/${idField}/features`, params).then((res) => {
      dispatch(featuresFetchedB(res.data))
    })
  }
}

export const removeFeatures = (idField) => {
  return (dispatch) => {
    return http.delete(`/field/${idField}/features`).then((res) => {
      if (res.data.deleted !== 0) {
        dispatch(clearFeatures())
        dispatch(featuresFetched())
        dispatch(addEvent('Boundary removed'))
      }
    })
  }
}

export function setYear(year) {
  return (dispatch, getState) => {
    const state = getState()
    const client = state?.client?.singleton
    const bboxOptions = { year: year }
    if (client) {
      bboxOptions['clientId'] = client.id
    }
    dispatch(fetchBBox(bboxOptions))
    dispatch(setYearAction(year))
    dispatch(clearHarvests())
  }
}

export const fetchBBox = (options?) => {
  return (dispatch) => {
    let params: { params: Record<string, unknown> } | undefined
    if (options?.clientId || options?.year) {
      params = { params: {} }
      if (options?.clientId) {
        params.params.clientId = options.clientId
      }
      if (options?.year) {
        params.params.year = options.year
      }
    }
    return http.get('/field/bbox', params).then((res) => {
      const { extent, features } = res.data

      if (!options?.clientId) {
        // Without a client the extent covers the whole tenant, which bounds
        // the static field boundary layer
        dispatch(setTenantExtent(extent))
      }
      dispatch(setDefaultExtent(extent))
    })
  }
}

/**
 * Refresh only the tenant-wide extent that bounds the static field boundary
 * layer, leaving defaultExtent (and so the selected client's viewport) alone.
 * Call it after a boundary write, which can widen the tenant's fields.
 */
export const fetchTenantExtent = () => {
  return (dispatch) => {
    return http
      .get('/field/bbox', { suppressErrorModal: true })
      .then((res) => {
        dispatch(setTenantExtent(res.data.extent))
      })
      .catch((error) => {
        console.error('Failed to refresh the tenant extent', error)
      })
  }
}

export const switchBoundariesForEdit = (options?) => {
  return (dispatch) => {
    let params: { params: Record<string, unknown> } | undefined
    if (options?.clientId || options?.year) {
      params = { params: {} }
      if (options?.clientId) {
        params.params.clientId = options.clientId
      }
      if (options?.year) {
        params.params.year = options.year
      }
    }
    return http.get('/field/bbox', params).then((res) => {
      if (options.callBack && typeof options.callBack === 'function') {
        options.callBack(options.field, options.isOpen)
        if (options?.field && options?.year)
          dispatch(fetchFeatures(options.field.id, options.year))
        if (options.year === null || options.year === undefined) {
          const currentYear = new Date().getFullYear()
          dispatch(setYearAction(currentYear))
        } else {
          dispatch(setYearAction(options.year))
        }
        dispatch(closeModal())
      }
    })
  }
}

export const fetchFeaturesByBbox = (bbox, year, farmId) => {
  return (dispatch) => {
    return http
      .get('/field/features', {
        params: {
          bbox: bbox ? bbox.join(',') : undefined,
          year: year,
          farmId: farmId,
        },
      })
      .then((res) => {
        dispatch(setFeaturesOverview(res.data))
      })
  }
}

export const filterFieldsCFFA = (year, crop, clientId, farmIds) => {
  return (dispatch) => {
    const params: { params: Record<string, unknown> } = {
      params: { year: year, crop: crop },
    }
    if (farmIds && farmIds.length) {
      params.params.farmId = `[${farmIds}]`
    } else if (clientId) {
      params.params.clientId = clientId
    }
    return http.get('/field', params).then((res) => {
      dispatch(fieldsHarvestedAFetched(res.data.results || res.data))
    })
  }
}

export const filterFieldsCFFB = (year, crop, clientId, farmIds) => {
  return (dispatch) => {
    const params: { params: Record<string, unknown> } = {
      params: { year: year, crop: crop },
    }
    if (farmIds && farmIds.length) {
      params.params.farmId = `[${farmIds}]`
    } else if (clientId) {
      params.params.clientId = clientId
    }
    return http.get('/field', params).then((res) => {
      dispatch(fieldsHarvestedBFetched(res.data.results || res.data))
    })
  }
}

export const getLayerInfoByHarvest = (id, year) => {
  return (dispatch) => {
    if (id && year) {
      const params = { params: { harvest_id: id, year: year } }
      return http.get('/layer/info', params).then((res) => {
        dispatch(layerInfoFetched(res.data))
      })
    }
  }
}

export const getLayerInfoAByHarvest = (id, year) => {
  return (dispatch) => {
    if (id && year) {
      const params = { params: { harvest_id: id, year: year } }
      return http.get('/layer/info', params).then((res) => {
        dispatch(layerInfoAFetched(res.data))
      })
    }
  }
}

export const getLayerInfoBByHarvest = (id, year) => {
  return (dispatch) => {
    if (id && year) {
      const params = { params: { harvest_id: id, year: year } }
      return http.get('/layer/info', params).then((res) => {
        dispatch(layerInfoBFetched(res.data))
      })
    }
  }
}

export const fetchFieldImport = (fileObj) => {
  return (dispatch) => {
    return http.post('/field/import', fileObj).then((res) => {
      dispatch(fieldsImported(res.data))
    })
  }
}

export const fetchPointInfo = (url) => {
  return (dispatch) => {
    return geoserverFetch(url, {
      method: 'GET',
      mode: 'cors',
    }).then((res) => {
      res.text().then((result) => {
        const data = JSON.parse(result)
        if (isEmpty(data.features)) {
          dispatch(pointInfoRes(undefined))
        } else {
          const { properties } = data.features[0]
          dispatch(pointInfoRes(properties))
        }
      })
    })
  }
}

export const fetchSwathInfo = (url, layersName, harvestId) => {
  return async (dispatch) => {
    try {
      const res = await geoserverFetch(url, {
        method: 'GET',
        mode: 'cors',
      })

      if (!res.ok) {
        throw new Error(
          `HTTP error fetching WMS feature info for swath! status: ${res.status}`,
        )
      }

      const result = await res.text()
      // Geoserver may return non-JSON on error even with INFO_FORMAT=application/json
      let data
      try {
        data = JSON.parse(result)
      } catch (e) {
        throw new Error('Response is not valid JSON')
      }

      if (isEmpty(data.features)) {
        dispatch(swathInfoRes(undefined))
      } else {
        const feature = data.features[0]
        const { properties } = feature
        const rowId = properties.row_index

        if (rowId && isGisUrl(url)) {
          // The proxy serves the row by harvest and index; it builds the WFS
          // request and its filter itself.
          const _harvestId = harvestId || properties.harvest_id
          const rowRes = await geoserverFetch(
            gisUrl(
              `/harvests/${_harvestId}/swath-rows/${encodeURIComponent(rowId)}`,
            ),
            { method: 'GET', mode: 'cors' },
          )
          if (!rowRes.ok) {
            console.warn(
              'Swath row fetch failed, falling back to WMS properties',
              rowRes.status,
            )
            dispatch(swathInfoRes(data))
          } else {
            dispatch(swathInfoRes(await rowRes.json()))
          }
        } else if (rowId) {
          try {
            const urlObj = new URL(url)
            // url is wms, need to rebuild with wfs
            const baseUrl = `${urlObj.origin}${urlObj.pathname.replace(
              /\/wms\/?$/i,
              '/wfs',
            )}`

            const _harvestId = harvestId || properties.harvest_id
            const filter = `row_index='${rowId}' AND harvest_id='${_harvestId}'`
            const cqlFilter = encodeURIComponent(filter)
            const encodedLayerName = encodeURIComponent(layersName)
            const wfsUrl = `${baseUrl}?service=WFS&version=2.0.0&request=GetFeature&outputFormat=application/json&typeNames=${encodedLayerName}&cql_filter=${cqlFilter}&srsName=EPSG:3857`
            const wfsRes = await geoserverFetch(wfsUrl, {
              method: 'GET',
              mode: 'cors',
            })

            if (!wfsRes.ok) {
              console.warn(
                'WFS fetch failed, falling back to WMS properties',
                wfsRes.status,
              )
              dispatch(swathInfoRes(data))
            } else {
              const swathData = await wfsRes.json()
              dispatch(swathInfoRes(swathData))
            }
          } catch (e) {
            console.error('Error constructing or fetching WFS URL', e)
            dispatch(swathInfoRes(data))
          }
        } else {
          dispatch(swathInfoRes(data))
        }
      }
    } catch (e) {
      console.error('Error fetching swath info', e)
      dispatch(swathInfoRes(undefined))
    }
  }
}

export const fetchAreaInfo = (url) => {
  return (dispatch) => {
    return geoserverFetch(url, {
      method: 'GET',
      mode: 'cors',
    }).then((res) => {
      res.text().then((result) => {
        const data = JSON.parse(result)
        if (isEmpty(data.features)) {
          dispatch(areaInfoRes(undefined))
        } else {
          dispatch(areaInfoRes(data))
        }
      })
    })
  }
}

export const fetchElevationProfile = (
  url,
  payload = null,
  contentType = 'text/plain',
) => {
  return (dispatch) => {
    const fetchOptions: RequestInit = {
      method: payload ? 'POST' : 'GET',
      mode: 'cors' as RequestMode,
    }
    if (payload) {
      fetchOptions.body = payload
      fetchOptions.headers = {
        'Content-Type': contentType,
      }
    }

    return geoserverFetch(url, fetchOptions)
      .then((res) => {
        if (!res.ok) {
          return res.text().then((errorBody) => {
            console.error(
              'Elevation Profile HTTP Error:',
              res.status,
              res.statusText,
              errorBody,
            )
            throw new Error(
              `Elevation profile request failed with status ${res.status}`,
            )
          })
        }
        return res.text().then((result) => {
          try {
            const data = JSON.parse(result)
            if (isEmpty(data)) {
              dispatch(elevationProfileRes(undefined))
            } else {
              dispatch(elevationProfileRes(data))
            }
          } catch (err) {
            console.error('Error parsing elevation profile WFS/WPS:', err)
            dispatch(elevationProfileRes(undefined))
          }
        })
      })
      .catch((err) => {
        console.error('Elevation Profile Fetch Error:', err)
        dispatch(elevationProfileRes(undefined))
      })
  }
}

export const exportLayer = (id, data) => {
  return (dispatch) => {
    downloadExport(id, data, dispatch)
  }
}

//Run function asynchronously so that some browsers (like Safari) don't kill the request
//if it doesn't return soon enough. Safari is 10 seconds for Synchronous requests which
//export used to be.
async function downloadExport(id, data, dispatch) {
  try {
    const result = await http.post(`/harvest/${id}/export`, data)
    if (result?.data?.url && result.data.url !== '') {
      const parts = result.data.url.split('/')
      dispatch(
        downloadingLayer({
          url: result.data.url,
          fileName: parts[parts.length - 1],
        }),
      )
    } else {
      dispatch(
        showModal(
          <Alert severity="error">{i18next.t('layer_unavailable')}</Alert>,
          { title: i18next.t('alert') },
        ),
      )
    }
  } catch (e) {
    dispatch(
      showModal(
        <Alert severity="error">{i18next.t('layer_download_error')}</Alert>,
        { title: i18next.t('alert') },
      ),
    )
  }
}
// The layer processor's log for a harvest's last run; farmtrx admins only
export const HARVEST_LOG_EXPORT_TYPE = 'harvest_log'

// The harvest log's download URL, or null when the harvest has none.
// Other errors come back to the caller, which shows them in its dialog.
export const getHarvestLogUrl = async (
  harvestId: number,
): Promise<string | null> => {
  try {
    const res = await http.post(
      `/harvest/${harvestId}/export`,
      { export_type: HARVEST_LOG_EXPORT_TYPE },
      { suppressErrorModal: true },
    )
    return res?.data?.url || null
  } catch (e) {
    if (e?.response?.status === 404) {
      return null
    }
    throw e
  }
}

export type HoverLayer = 'raw_points' | 'corrected_points' | 'swath'

export interface HarvestLayerData {
  url: string
  compressedSize: number | null
  uncompressedSize: number | null
}

// Unlike exportLayer (whose *_fgb types are zipped downloads) this doesn't
// download anything: the URL points at a gzip-encoded FlatGeobuf the hover
// inspection worker streams in itself.
// Errors come back to the caller, which shows them in the tool.
export const getHarvestLayerData = async (
  harvestId: number,
  layer: HoverLayer,
): Promise<HarvestLayerData> => {
  const res = await http.post(
    `/harvest/${harvestId}/export`,
    { export_type: `${layer}_fgb_stream` },
    { suppressErrorModal: true },
  )
  if (!res?.data?.url) {
    throw new Error('layer_unavailable')
  }
  return {
    url: res.data.url,
    compressedSize: res.data.compressed_size ?? null,
    uncompressedSize: res.data.uncompressed_size ?? null,
  }
}

export const archiveFields = (fieldIds, callback) => {
  return (dispatch) => {
    return http.post(`/field/archive`, { fieldIds }).then((res) => {
      dispatch(removeFields(fieldIds))
      dispatch(closeModal())
      dispatch(fetchBBox())
      callback && callback()
    })
  }
}
export const unArchiveFields = (fieldIds, callback) => {
  return (dispatch) => {
    return http.post(`/field/restore`, { fieldIds }).then((res) => {
      dispatch(closeModal())
      dispatch(fetchBBox())
      callback && callback()
    })
  }
}

export const exportFieldBoundary = (fieldIds: number | number[]) => {
  const ids = Array.isArray(fieldIds) ? fieldIds : [fieldIds]
  return http.post(
    '/field/export',
    { fieldIds: ids },
    { responseType: 'arraybuffer' },
  )
}
